/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState, useCallback } from 'react'
import _ from 'lodash'

import { ActionButton, ViewWrapper, PageWrapper, ViewSpacerWrapper, AddDeviceQRC } from 'components/widgets'
import { ALL_ORGS_SELECTED, MOBILE_APP_ENABLE, USERS_PATH } from 'lib/constants'
import { isAdmin, isInstaller, isSuperAdmin } from 'lib/utils/auth'
import { DetailsDrawer, Grants, Orgs, Modal, PopUpNotifications } from 'models'
import { getUsersPage, getUserById, getV5UsersCSV, USERS_PAGE_SIZE } from 'models/api/users'
import * as utils from './utils'
import { PreConfiguredTableView } from 'components/partials/TableViewV2'
import {
  Grant,
  RouteParams,
  SortOrder,
  TableState,
  TableToolEvent,
  trans,
  TranslationGroup,
  TranslationKey,
  User,
} from 'lib/types'
import { invertBinaryEnum } from 'lib/utils/common'
import { UserDetailDrawer } from 'components/partials/DrawerDetails'
import { AddUserModal } from 'components/partials/AddUserModal'
import { useHistory, useParams } from 'react-router-dom'
import i18n from 'i18n.js'
import { RenderCounter } from 'components/widgets/RenderCounter'
import { UsersColumnKeys } from 'lib/constants/keys/users'
import { useGlobal } from 'contexts/GlobalProvider'

function UsersPage() {
  const translation: TranslationGroup = trans.group(TranslationKey.USER_TABLE_VIEW)
  const common: TranslationGroup = trans.common()
  const history = useHistory()
  const { id } = useParams<RouteParams>()
  const { selectedOrgId, refreshFlag } = useGlobal()
  const orgs = Orgs.use(({ orgs }) => orgs)
  const userOrgsMap = Grants.useUserOrgMap()
  // v5 /users does not embed grants, so we rebuild per-user grants from the Grants store.
  // We build it ourselves (instead of calling useSelectedGrantsByUserId) so the result
  // has a stable reference across renders — otherwise filteredUsers gets a new ref every
  // render and the V2 PaginatedTable's internal [sortedData] effect thrashes.
  const allGrants = Grants.use(({ grants }) => grants)
  const grantsByUserId = useMemo(() => {
    const scoped =
      !selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED
        ? allGrants
        : allGrants.filter(g => g.attributes.orgId === selectedOrgId)
    const res: { [userId: string]: Grant[] } = {}
    scoped.forEach(g => {
      if (!res[g.attributes.userId]) res[g.attributes.userId] = []
      res[g.attributes.userId].push(g)
    })
    return res
  }, [allGrants, selectedOrgId])

  const {
    setModalContent,
    openModal: openQrcModal,
    closeModal: closeQrcModal,
  } = Modal.useStaticModal(translation.enable_sso)

  const getEnableAppURL = () => {
    const url = new URL(window.location.href)
    const base = `${url.protocol}//${url.hostname}`
    const port = url.port ? `:${url.port}` : ''
    return `${base}${port}${MOBILE_APP_ENABLE}`
  }

  useEffect(() => {
    setModalContent(
      <AddDeviceQRC
        responders={true}
        closeModal={closeQrcModal}
        value={getEnableAppURL()}
        subtitle={translation.enable_app_login}
      />,
    )
  }, [closeQrcModal, setModalContent])

  const hasAdminGrants = isAdmin(selectedOrgId)
  const [AddUserModalOpen, setAddUserModalOpen] = useState(false)

  const [tableState, setTableState] = useState<TableState<string, UsersColumnKeys>>({
    filterBy: utils.getFilterOptions()[0],
    sortBy: utils.getSortOptions()[0].sortBy,
    searchBy: '',
  })

  // --- Server-side pagination state ---
  const [pageUsers, setPageUsers] = useState<User[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  // UsersColumnKeys → server-side User schema field name (camelCased on v5).
  // Columns without a server equivalent (Membership, Roles) are omitted; sort still
  // shows the option in the dropdown but we just don't pass it to the server.
  const SERVER_SORT_FIELD: { [k: string]: string } = {
    [UsersColumnKeys.Name]: 'firstName',
    [UsersColumnKeys.Email]: 'email',
    [UsersColumnKeys.Provider]: 'provider',
    [UsersColumnKeys.LastCheckIn]: 'lastCheckedInAt',
  }

  const loadPage = useCallback(
    (page: number, overrideState?: TableState<string, UsersColumnKeys>) => {
      const state = overrideState ?? tableState
      const serverField = SERVER_SORT_FIELD[state.sortBy.field as string]
      setLoading(true)
      getUsersPage({
        page,
        orgId: selectedOrgId,
        search: state.searchBy || undefined,
        sortBy: serverField ? { field: serverField, order: state.sortBy.order } : undefined,
      })
        .then(result => {
          setPageUsers(result.users)
          setCurrentPage(result.currentPage)
          setTotalCount(result.totalCount)
        })
        .catch(err => PopUpNotifications.fireErrorObject(err))
        .finally(() => setLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrgId, tableState],
  )

  // Reset to page 1 whenever the globally-selected org or any tool state (search/sort/
  // filter) changes. loadPage's identity changes when [selectedOrgId, tableState] do,
  // so this fires exactly when we want a fresh page-1 fetch.
  useEffect(() => {
    loadPage(1)
  }, [loadPage])

  // Honor the global refresh signal (top-bar Refresh button, add-user chain,
  // delete-user chain, etc). When anything flips refreshFlag to true, refetch
  // the currently-visible page so the user sees fresh data.
  useEffect(() => {
    if (refreshFlag) loadPage(currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag])

  // Deep-linked user id: open the detail drawer. If the user isn't in the current page, fetch by id.
  useEffect(() => {
    if (!id || loading) return
    const fromPage = pageUsers.find(u => u.id === id)
    if (fromPage) {
      openDrawer(fromPage)
    } else {
      getUserById(id)
        .then(u => openDrawer(u))
        .catch(() => {
          PopUpNotifications.fireWarning({ content: `${common.user} ${id}${common.was_not_found}` })
          history.replace(USERS_PATH)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loading])

  i18n.on('languageChanged', lng => {
    setTableState({
      ...tableState,
      filterBy: utils.getFilterOptions()[0],
      sortBy: utils.getSortOptions()[0].sortBy,
    })
  })

  function handleDrawerClose() {
    history.push({ pathname: `${USERS_PATH}` })
    DetailsDrawer.close()
  }

  function openDrawer(row: User) {
    DetailsDrawer.show({
      drawerComponent: UserDetailDrawer,
      drawerProps: {
        user: row,
        close: handleDrawerClose,
      },
    })
  }

  function handleRowClick(row: User) {
    if (row.id != id) {
      history.push({ pathname: `${USERS_PATH}/${row.id}` })
    }
    openDrawer(row)
  }

  function handleHeaderClick(header: string) {
    setTableState(state =>
      Object.assign({}, state, {
        sortBy: {
          field: header,
          order: state.sortBy.field === header ? invertBinaryEnum(state.sortBy.order) : SortOrder.Ascending,
        },
      }),
    )
  }

  function handleToolEvent(toolEvent: TableToolEvent<string, UsersColumnKeys>) {
    setTableState(state => ({
      filterBy: toolEvent.filterByRequest || state.filterBy,
      sortBy: toolEvent.sortByRequest || state.sortBy,
      searchBy: _.isNil(toolEvent.searchByRequest) ? state.searchBy : toolEvent.searchByRequest,
    }))
  }

  // Search and sort happen server-side via getUsersPage params. We still apply the
  // "no_membership" client-side filter on the loaded page (no server equivalent) and
  // hydrate `membership` / `grants` per user for column rendering and CSV export.
  // Memoized so the reference is stable across renders — V2 PaginatedTable relies on
  // sortedData reference equality to avoid re-running its internal page sync effect.
  const filteredUsers = useMemo(() => {
    return pageUsers
      .filter(user => utils.getFilterUserComparison(user, tableState.filterBy))
      .map(u => {
        const userOrgs = userOrgsMap[u.id] || []
        u.attributes.membership = orgs.filter(o => userOrgs.includes(o.id))
        u.attributes.isOnline = false
        u.attributes.grants = (grantsByUserId[u.id] || []).map(g => ({
          id: g.id,
          name: g.attributes.name,
          org_id: g.attributes.orgId,
          user_id: g.attributes.userId,
        }))
        return u
      })
  }, [pageUsers, tableState.filterBy, userOrgsMap, orgs, grantsByUserId])

  const configs = utils.getAdminUsersColumnKeysConfig(selectedOrgId || ALL_ORGS_SELECTED, handleRowClick)
  const adminRequiredCols = hasAdminGrants ? [] : configs.filter(col => !!col.requiresAdmin)
  const orgRequiredCols = selectedOrgId !== ALL_ORGS_SELECTED ? [] : configs.filter(col => !!col.requiresOrg)
  const hiddenColumns = adminRequiredCols.map(c => c.header).concat(orgRequiredCols.map(c => c.header))
  const isAdminOrInstaller = isAdmin(selectedOrgId) || isInstaller(selectedOrgId) || isSuperAdmin()

  return (
    <PageWrapper>
      <ViewWrapper>
        <RenderCounter name={'UsersPage'} />
        <ViewSpacerWrapper>
          <PreConfiguredTableView
            sortedData={filteredUsers}
            tableState={tableState}
            columnConfigs={configs}
            filterOptions={utils.getFilterOptions()}
            sortOptions={utils.getSortOptions()}
            onHeaderClick={handleHeaderClick}
            onRowClick={() => null}
            onToolEvent={handleToolEvent}
            hiddenColumns={hiddenColumns}
            selectedRowId={id}
            loading={loading}
            rowsPerPage={USERS_PAGE_SIZE}
            page={currentPage}
            pagination={{ currentPage, totalCount }}
            onPageChange={(_prev, newPage) => loadPage(newPage)}
          >
            <ActionButton onClick={() => void getV5UsersCSV(selectedOrgId || ALL_ORGS_SELECTED)}>
              {common.download_csv}
            </ActionButton>
            {hasAdminGrants && (
              <ActionButton
                onClick={
                  !selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED
                    ? () => alert(translation.all_properties_selected)
                    : () => setAddUserModalOpen(true)
                }
              >
                {translation.add_user}
              </ActionButton>
            )}
            {isAdminOrInstaller && (
              <ActionButton
                onClick={() => {
                  openQrcModal()
                }}
              >
                {translation.enable_app_login}
              </ActionButton>
            )}
            {AddUserModalOpen && (
              <AddUserModal
                orgId={selectedOrgId || ALL_ORGS_SELECTED}
                open={AddUserModalOpen}
                close={() => setAddUserModalOpen(false)}
                // The page is refreshed via the global refreshFlag at the end of the
                // AddUserModal.handleSubmit chain — no need to refetch on close, which
                // fires before the POST completes.
              />
            )}
          </PreConfiguredTableView>
        </ViewSpacerWrapper>
      </ViewWrapper>
    </PageWrapper>
  )
}

export default UsersPage
